import { motion } from "framer-motion";
import { STATS } from "@/lib/constants";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { useCountUp } from "@/lib/useCountUp";

const format = (n) => n.toLocaleString("en-US");
const StatCard = ({ item, index }) => {
  const [ref, value] = useCountUp(item.value, {
    duration: 1800,
    delay: index * 120,
  });
  return (
    <motion.div
      variants={fadeUp}
      ref={ref}
      data-testid={`stat-card-${index}`}
      className="group relative flex flex-col justify-between overflow-hidden rounded-sm border border-[color:var(--line)] bg-bg-2 p-8 md:p-10 min-h-[220px] md:min-h-[260px] transition-all duration-700 hover:border-[color:var(--gold)]/60"
    >
      {/* Corner index */}
      <span className="font-editorial absolute right-4 top-3 text-lg text-[color:var(--gold-hi)]/60">
        0{index + 1}
      </span>

      <div className="relative">
        <div className="flex items-baseline gap-1">
          <span className="font-display text-[3rem] md:text-[4.2rem] leading-[0.9] font-black tracking-tighter text-shine">
            {format(value)}
          </span>
          <span className="font-display text-2xl md:text-3xl font-black text-gold">
            {item.suffix}
          </span>
        </div>
      </div>

      <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.28em] text-muted">
        {item.label}
      </p>

      {/* Hover shimmer */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(circle at 30% 20%, rgba(201,169,97,0.14), transparent 55%)",
        }}
      />
    </motion.div>
  );
};

export const Stats = () => {
  return (
    <section
      id="journey"
      data-testid="stats-section"
      className="relative w-full bg-bg py-24 md:py-32 border-t border-[color:var(--line)]"
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-10">
        <motion.div
          variants={stagger(0.1, 0.05)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <motion.div variants={fadeUp} className="max-w-2xl">
            <p className="mb-3 inline-flex items-center gap-3 text-[10px] font-semibold tracking-[0.32em] uppercase text-gold">
              <span className="h-px w-8 bg-[color:var(--gold)]/70" />
              Chapter 02 — Journey
            </p>
            <h2
              data-testid="stats-title"
              className="font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-ink"
            >
              Career{" "}
              <span className="font-editorial text-gold-hi">highlights</span>
            </h2>
          </motion.div>
          <motion.p variants={fadeUp} className="max-w-md text-sm text-muted leading-relaxed">
            Numbers that measure a growing international presence — from
            monthly listeners to streaming milestones.
          </motion.p>
        </motion.div>

        <motion.div
          variants={stagger(0.1, 0.1)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
        >
          {STATS.map((item, i) => (
            <StatCard key={item.label} item={item} index={i} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};
